import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * ProgressBar Component
 * 
 * Displays an animated progress bar with gradient (blue-cyan) styling.
 * Follows the Instituto Valparaíso design system.
 * 
 * @param {number} progress - Progress percentage (0-100)
 * @param {boolean} showPercentage - Whether to show the percentage text (default: true)
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} className - Additional CSS classes
 */
export default function ProgressBar({ 
  progress = 0, 
  showPercentage = true, 
  size = 'md',
  className = '' 
}) {
  // Clamp progress between 0 and 100
  const clampedProgress = Math.min(100, Math.max(0, progress));
  
  // Size variants for the track height
  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  // Text size variants
  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Progress bar container */}
      <div className="flex items-center gap-3">
        {/* Track */}
        <div 
          className={`flex-1 ${sizeClasses[size]} rounded-full bg-white/10 overflow-hidden`}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {/* Animated fill with gradient */}
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
            initial={{ width: 0 }}
            animate={{ width: `${clampedProgress}%` }}
            transition={{ 
              duration: 0.8, 
              ease: 'easeOut',
              delay: 0.1 
            }}
          />
        </div>
        
        {/* Percentage text */}
        {showPercentage && (
          <motion.span 
            className={`${textSizeClasses[size]} font-medium text-white/80 min-w-[3rem] text-right`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            {Math.round(clampedProgress)}%
          </motion.span>
        )}
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  progress: PropTypes.number,
  showPercentage: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};
